import { Navigate, useLocation, Outlet } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getEffectiveRole, Role } from '@/lib/roles';

export default function ProtectedRouteNew({
  requireRole,
  children,
  loadingFallback = null,
}: { requireRole?: Role; children?: ReactNode; loadingFallback?: ReactNode }) {
  const { user, profile, loading, isAdminUser } = useAuth();
  const loc = useLocation();
  
  if (loading) return <>{loadingFallback}</>;
  if (!user) return <Navigate to="/coming-soon" state={{ from: loc }} replace />;

  // ✅ Acting allowed for admins here (so they can view employer/job-seeker areas)
  const role = getEffectiveRole(profile, { allowActing: true });
  
  // If this route requires admin, force admin overview
  if (requireRole === 'admin') {
    if (!loc.pathname.startsWith('/admin')) {
      return <Navigate to="/admin/overview" replace />;
    }
    return children ? <>{children}</> : <Outlet />;
  }
  
  // If this route requires employer/job_seeker, verify acting or real role matches
  if (requireRole && role && role !== requireRole) {
    // Mismatch → send to that role's dashboard
    const dest = role === 'employer' ? '/employer/dashboard'
             : role === 'job_seeker' ? '/job-seeker/dashboard'
             : '/admin/overview';
    return <Navigate to={dest} replace />;
  }
  
  // If children are passed, render them; otherwise use Outlet for nested routes
  return children ? <>{children}</> : <Outlet />;
}
